import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { handleFirestoreError } from '../utils/firestoreErrorHandler';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from './usePermissions';
import type {
  ManufacturingOrder,
  ManufacturingOrderFirestore,
  ManufacturingOrderLine,
  ManufacturingOrderStatus,
} from '../types/manufacturing';
import type { Product } from '../types/inventory';

function toOrder(docId: string, data: ManufacturingOrderFirestore): ManufacturingOrder {
  return {
    id: docId,
    businessId: data.businessId,
    status: data.status,
    createdByUserId: data.createdByUserId,
    prestationId: data.prestationId,
    transactionGroupId: data.transactionGroupId,
    serviceId: data.serviceId,
    serviceName: data.serviceName,
    clientId: data.clientId,
    clientName: data.clientName,
    assignedToUserId: data.assignedToUserId,
    assignedToName: data.assignedToName,
    plannedLines: data.plannedLines,
    actualLines: data.actualLines || [],
    stockApplied: data.stockApplied === true,
    stockMovementIds: data.stockMovementIds,
    createdAt: data.createdAt?.toDate(),
    updatedAt: data.updatedAt?.toDate(),
    stockAppliedAt: data.stockAppliedAt?.toDate(),
  };
}

export const useManufacturingOrders = (filters?: { status?: ManufacturingOrderStatus }) => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['manufacturing', 'orders', businessId, filters],
    queryFn: async () => {
      if (!businessId) return [];
      const q = query(
        collection(db, COLLECTIONS.manufacturingOrders),
        where('businessId', '==', businessId),
        ...(filters?.status ? [where('status', '==', filters.status)] : []),
        orderBy('updatedAt', 'desc')
      );
      const snap = await getDocs(q).catch((error) => {
        handleFirestoreError(error, 'useManufacturingOrders');
        throw error;
      });
      return snap.docs.map((d) => toOrder(d.id, d.data() as ManufacturingOrderFirestore));
    },
    enabled: !!businessId && (permissions?.canViewManufacturingOrders ?? false),
  });
};

export const useManufacturingOrder = (orderId: string | undefined) => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['manufacturing', 'order', businessId, orderId],
    queryFn: async () => {
      if (!businessId || !orderId) return null;
      const ref = doc(db, COLLECTIONS.manufacturingOrders, orderId);
      const snap = await getDoc(ref).catch((error) => {
        handleFirestoreError(error, 'useManufacturingOrder');
        throw error;
      });
      if (!snap.exists()) return null;
      const data = snap.data() as ManufacturingOrderFirestore;
      if (data.businessId !== businessId) return null;
      return toOrder(snap.id, data);
    },
    enabled: !!businessId && !!orderId && (permissions?.canViewManufacturingOrders ?? false),
  });
};

export const useCreateManufacturingOrder = () => {
  const queryClient = useQueryClient();
  const { currentBusiness, userProfile } = useAuth();
  const businessId = currentBusiness?.id;

  return useMutation({
    mutationFn: async (params: Omit<ManufacturingOrder, 'id' | 'businessId' | 'createdAt' | 'updatedAt' | 'stockAppliedAt'>) => {
      if (!businessId) throw new Error('Business non défini');
      const now = Timestamp.now();
      const payload: ManufacturingOrderFirestore = {
        ...params,
        businessId,
        status: params.status ?? 'draft',
        actualLines: params.actualLines || [],
        stockApplied: params.stockApplied === true,
        createdByUserId: params.createdByUserId ?? userProfile?.id,
        createdAt: now,
        updatedAt: now,
        ...(params.stockApplied ? { stockAppliedAt: now } : {}),
      };

      const ref = await addDoc(collection(db, COLLECTIONS.manufacturingOrders), payload);
      return ref.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manufacturing', 'orders', businessId] });
    },
  });
};

export const useUpdateManufacturingOrder = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;

  return useMutation({
    mutationFn: async (params: {
      id: string;
      patch: Partial<Pick<ManufacturingOrder, 'status' | 'assignedToUserId' | 'assignedToName' | 'plannedLines' | 'actualLines'>>;
      clearAssignee?: boolean;
    }) => {
      if (!businessId) throw new Error('Business non défini');
      const ref = doc(db, COLLECTIONS.manufacturingOrders, params.id);
      const snap = await getDoc(ref);
      if (!snap.exists()) throw new Error('Ordre introuvable');
      const data = snap.data() as ManufacturingOrderFirestore;
      if (data.businessId !== businessId) throw new Error('Accès refusé');
      if (data.stockApplied === true) throw new Error('Ordre verrouillé (stock déjà appliqué)');

      const payload: Record<string, unknown> = { ...params.patch, updatedAt: Timestamp.now() };
      if (params.clearAssignee) {
        payload.assignedToUserId = deleteField();
        payload.assignedToName = deleteField();
      }
      await updateDoc(ref, payload as never);
      return params.id;
    },
    onSuccess: (_id, variables) => {
      queryClient.invalidateQueries({ queryKey: ['manufacturing', 'orders', businessId] });
      queryClient.invalidateQueries({ queryKey: ['manufacturing', 'order', businessId, variables.id] });
    },
  });
};

export const useCloseManufacturingOrder = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;

  return useMutation({
    mutationFn: async (params: {
      id: string;
      actualLines: ManufacturingOrderLine[];
      employeeId?: string;
      employeeName?: string;
      date?: Date;
    }) => {
      if (!businessId) throw new Error('Business non défini');
      const orderRef = doc(db, COLLECTIONS.manufacturingOrders, params.id);
      const now = Timestamp.now();
      const movementDate = Timestamp.fromDate(params.date ?? new Date());
      const finalizedLines = params.actualLines
        .map((line) => ({
          ...line,
          quantity: Math.max(0, Math.floor(Number(line.quantity))),
        }))
        .filter((line) => !!line.productId && line.quantity > 0);
      if (finalizedLines.length === 0) {
        throw new Error('Aucune ligne valide à clôturer');
      }

      const uniqueProductIds = Array.from(new Set(finalizedLines.map((l) => l.productId)));
      // 1 update produit / produit unique + 1 mouvement / ligne + 1 update OF
      const writeOps = uniqueProductIds.length + finalizedLines.length + 1;
      if (writeOps > 450) {
        throw new Error('Trop de lignes pour une clôture atomique. Veuillez scinder cet ordre.');
      }

      const movementRefs = finalizedLines.map(() => doc(collection(db, COLLECTIONS.stockMovements)));
      const movementIds = movementRefs.map((m) => m.id);

      /**
       * Transaction atomique unique :
       * - lectures (OF + tous les produits) d’abord
       * - puis écritures (stocks, mouvements, OF terminé)
       * Si une étape échoue → rien n’est persisté (tout ou rien).
       */
      await runTransaction(db, async (tx) => {
        // ——— LECTURES ———
        const orderSnap = await tx.get(orderRef);
        if (!orderSnap.exists()) throw new Error('Ordre introuvable');
        const orderData = orderSnap.data() as ManufacturingOrderFirestore;
        if (orderData.businessId !== businessId) throw new Error('Accès refusé');
        if (orderData.stockApplied === true) throw new Error('Stock déjà appliqué sur cet ordre');

        const productSnaps = new Map<string, Awaited<ReturnType<typeof tx.get>>>();
        for (const productId of uniqueProductIds) {
          productSnaps.set(productId, await tx.get(doc(db, COLLECTIONS.products, productId)));
        }

        // ——— VALIDATION EN MÉMOIRE (aucune écriture) ———
        type StockState = {
          currentStock: number;
          stockBySite: Record<string, number>;
          productName: string;
        };
        const stockState = new Map<string, StockState>();

        for (const productId of uniqueProductIds) {
          const productSnap = productSnaps.get(productId)!;
          if (!productSnap.exists()) {
            const sample = finalizedLines.find((l) => l.productId === productId);
            throw new Error(`Produit introuvable: ${sample?.productName || productId}`);
          }
          const productData = productSnap.data() as Product;
          if (productData.businessId !== businessId) {
            throw new Error(
              `Le produit "${String(productData.name || productId)}" n'appartient pas à votre business.`
            );
          }
          if (productData.isActive !== true) {
            throw new Error(`Le produit "${String(productData.name || productId)}" est désactivé.`);
          }
          stockState.set(productId, {
            currentStock: Number(productData.currentStock || 0),
            stockBySite: { ...((productData.stockBySite as Record<string, number> | undefined) || {}) },
            productName: String(productData.name || productId),
          });
        }

        type PreparedLine = {
          line: (typeof finalizedLines)[number];
          index: number;
          resolvedSiteId: string;
          resolvedSiteName: string;
        };
        const prepared: PreparedLine[] = [];

        for (let index = 0; index < finalizedLines.length; index += 1) {
          const line = finalizedLines[index];
          const state = stockState.get(line.productId);
          if (!state) {
            throw new Error(`Produit introuvable: ${line.productName || line.productId}`);
          }

          const resolvedSiteId = String(line.siteId ?? '').trim();
          const resolvedSiteName = String(line.siteName ?? '').trim();
          if (!resolvedSiteId || !resolvedSiteName) {
            throw new Error(
              `Précisez le lieu (identifiant et libellé) pour la ligne « ${line.productName || line.productId} ».`
            );
          }

          const availableAtSite = state.stockBySite[resolvedSiteId] ?? state.currentStock;
          if (availableAtSite < line.quantity) {
            throw new Error(
              `Stock insuffisant pour "${line.productName || state.productName}". Disponible: ${availableAtSite}, demandé: ${line.quantity}`
            );
          }

          const currentAtSite = state.stockBySite[resolvedSiteId] ?? state.currentStock;
          state.currentStock = Math.max(0, state.currentStock - line.quantity);
          state.stockBySite[resolvedSiteId] = Math.max(0, currentAtSite - line.quantity);
          prepared.push({ line, index, resolvedSiteId, resolvedSiteName });
        }

        // ——— ÉCRITURES (commit atomique unique) ———
        for (const productId of uniqueProductIds) {
          const state = stockState.get(productId)!;
          tx.update(doc(db, COLLECTIONS.products, productId), {
            currentStock: state.currentStock,
            stockBySite: state.stockBySite,
            updatedAt: now,
          } as any);
        }

        for (const { line, index, resolvedSiteId, resolvedSiteName } of prepared) {
          tx.set(movementRefs[index], {
            businessId,
            productId: line.productId,
            productName: line.productName,
            type: 'exit',
            quantity: line.quantity,
            reason: 'Consommation fabrication',
            date: movementDate,
            ...(params.employeeId ? { employeeId: params.employeeId } : {}),
            ...(params.employeeName ? { employeeName: params.employeeName } : {}),
            ...(orderData.prestationId ? { prestationId: orderData.prestationId } : {}),
            ...(orderData.transactionGroupId
              ? { transactionGroupId: orderData.transactionGroupId }
              : {}),
            createdAt: now,
            manufacturingOrderId: params.id,
            siteId: resolvedSiteId,
            siteName: resolvedSiteName,
            ...(line.lot ? { lot: line.lot } : {}),
          });
        }

        tx.update(orderRef, {
          status: 'done',
          actualLines: finalizedLines,
          stockApplied: true,
          stockAppliedAt: now,
          stockMovementIds: movementIds,
          updatedAt: now,
        } as any);
      });

      return { id: params.id, stockMovementIds: movementIds };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['manufacturing', 'orders', businessId] });
      queryClient.invalidateQueries({
        queryKey: ['manufacturing', 'order', businessId, variables.id],
      });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'stockMovements', businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products', businessId] });
    },
  });
};

export const useDeleteManufacturingOrder = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;

  return useMutation({
    mutationFn: async (orderId: string) => {
      if (!businessId) throw new Error('Business non défini');
      const ref = doc(db, COLLECTIONS.manufacturingOrders, orderId);
      const snap = await getDoc(ref);
      if (!snap.exists()) throw new Error('Ordre introuvable');
      const data = snap.data() as ManufacturingOrderFirestore;
      if (data.businessId !== businessId) throw new Error('Accès refusé');
      if (data.stockApplied === true) {
        throw new Error('Impossible de supprimer un ordre dont le stock a déjà été appliqué');
      }
      await deleteDoc(ref);
      return orderId;
    },
    onSuccess: (orderId) => {
      queryClient.invalidateQueries({ queryKey: ['manufacturing', 'orders', businessId] });
      queryClient.removeQueries({ queryKey: ['manufacturing', 'order', businessId, orderId] });
    },
  });
};

